import { db } from '@/firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Estado de onboarding por usuario (userPreferences/{uid}.onboarding).
 *
 * - Se guarda por UID, nunca por workspace: el progreso es de la cuenta,
 *   no del workspace activo.
 * - loadOnboardingState es SOLO lectura: jamas crea el documento. La
 *   ausencia del campo onboarding (o del documento) es la senal de
 *   "primera entrada".
 * - saveOnboardingState es la unica via de escritura y siempre lo hace
 *   sobre el documento del propio UID, con merge para preservar cualquier
 *   otro campo futuro de userPreferences/{uid}.
 */

export const CURRENT_TUTORIAL_EXPERIENCE_VERSION = 2;

export const DEFAULT_ONBOARDING = {
  completedTutorials: [],
  onboardingDismissed: false,
  followUpReminderShown: false,
  tutorialExperienceVersionSeen: 0,
  tutorialExperienceReminderVersionShown: 0,
};

function isValidUid(uid) {
  return typeof uid === 'string' && uid.trim().length > 0;
}

/**
 * Normaliza defensivamente el subobjeto onboarding leido de Firestore.
 * Tolera datos parciales/corruptos y elimina duplicados de completedTutorials.
 */
function normalizeOnboarding(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};

  let completedTutorials = [];
  if (Array.isArray(src.completedTutorials)) {
    const seen = new Set();
    completedTutorials = src.completedTutorials.filter((id) => {
      if (typeof id !== 'string' || id.trim().length === 0) return false;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }

  return {
    completedTutorials,
    onboardingDismissed: src.onboardingDismissed === true,
    followUpReminderShown: src.followUpReminderShown === true,
    tutorialExperienceVersionSeen:
      typeof src.tutorialExperienceVersionSeen === 'number' &&
      Number.isFinite(src.tutorialExperienceVersionSeen)
        ? src.tutorialExperienceVersionSeen
        : 0,
    tutorialExperienceReminderVersionShown:
      typeof src.tutorialExperienceReminderVersionShown === 'number' &&
      Number.isFinite(src.tutorialExperienceReminderVersionShown)
        ? src.tutorialExperienceReminderVersionShown
        : 0,
  };
}

/**
 * Lee el estado de onboarding del usuario SIN crear documentos.
 * @param {string} uid
 * @returns {Promise<{exists:boolean, isFirstEntry:boolean, onboarding:object}>}
 */
export async function loadOnboardingState(uid) {
  if (!isValidUid(uid)) {
    throw new Error('loadOnboardingState: se requiere un uid valido');
  }

  const ref = doc(db, 'userPreferences', uid);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    return {
      exists: false,
      isFirstEntry: true,
      onboarding: { ...DEFAULT_ONBOARDING },
    };
  }

  const data = snap.data() || {};
  const hasOnboarding =
    data &&
    typeof data === 'object' &&
    data.onboarding &&
    typeof data.onboarding === 'object';

  if (!hasOnboarding) {
    return {
      exists: false,
      isFirstEntry: true,
      onboarding: { ...DEFAULT_ONBOARDING },
    };
  }

  return {
    exists: true,
    isFirstEntry: false,
    onboarding: normalizeOnboarding(data.onboarding),
  };
}

/**
 * Guarda el estado de onboarding del propio usuario (merge, por UID).
 * Normaliza antes de escribir y actualiza updatedAt con serverTimestamp.
 * @param {string} uid
 * @param {object} onboarding
 * @returns {Promise<void>}
 */
export async function saveOnboardingState(uid, onboarding) {
  if (!isValidUid(uid)) {
    throw new Error('saveOnboardingState: se requiere un uid valido');
  }

  const normalized = normalizeOnboarding(onboarding);
  const ref = doc(db, 'userPreferences', uid);

  await setDoc(
    ref,
    {
      onboarding: {
        ...normalized,
        updatedAt: serverTimestamp(),
      },
    },
    { merge: true }
  );
}
